import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, RefreshCw, Download, Zap, Wand2 } from "lucide-react";
import { LogoMark } from "@/components/site/Logo";
import { api, API, errorText } from "@/lib/api";
import { STYLES } from "@/data/site";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const STATUSES = ["awaiting_payment", "paid", "in_progress", "delivered", "cancelled"];
const BADGE = {
  awaiting_payment: "bg-slate-500/20 text-slate-200",
  paid: "bg-[#3b4049]/25 text-[#e6e8ec]",
  in_progress: "bg-amber-400/15 text-amber-200",
  delivered: "bg-emerald-400/15 text-emerald-200",
  cancelled: "bg-red-500/15 text-red-200",
};
const label = (s) => s.replace(/_/g, " ");

const OrderCard = ({ o, onStatus, onRegenerate }) => {
  const photo = `${API}/admin/orders/${o.id}/photo`;
  const result = `${photo}?kind=result`;
  const aiBadge = { done: "AI done", processing: "AI editing", queued: "AI queued", failed: "AI failed" }[o.ai_status];
  return (
    <div data-testid={`admin-order-${o.id}`} className="glass rounded-3xl overflow-hidden grid sm:grid-cols-[200px_1fr]">
      <div className="grid grid-rows-2 bg-black h-72 sm:h-full">
        <a href={photo} target="_blank" rel="noreferrer" className="relative block" data-testid={`admin-order-photo-${o.id}`}>
          <img src={photo} alt={o.photo_filename} className="h-full w-full object-cover" loading="lazy" />
          <span className="absolute bottom-1.5 left-1.5 text-[11px] px-2 py-0.5 rounded-full bg-black/70">Original</span>
        </a>
        {o.result_path ? (
          <a href={result} target="_blank" rel="noreferrer" className="relative block" data-testid={`admin-order-result-${o.id}`}>
            <img src={result} alt="AI result" className="h-full w-full object-cover" loading="lazy" />
            <span className="absolute bottom-1.5 left-1.5 text-[11px] px-2 py-0.5 rounded-full bg-[#3b4049]">AI result</span>
          </a>
        ) : (
          <div className="grid place-items-center text-xs text-slate-400">No result yet</div>
        )}
      </div>
      <div className="p-5 sm:p-6 space-y-3 min-w-0">
        <div className="flex flex-wrap items-center gap-2 justify-between">
          <div className="font-display font-semibold truncate">{o.name}</div>
          <span data-testid="admin-order-status-badge" className={`text-xs px-2.5 py-1 rounded-full capitalize ${BADGE[o.status] || BADGE.awaiting_payment}`}>{label(o.status)}</span>
        </div>
        <div className="flex flex-wrap gap-2">
          {o.rush && <span data-testid="admin-order-rush-badge" className="text-xs px-2.5 py-1 rounded-full bg-[#3b4049] flex items-center gap-1"><Zap className="h-3 w-3" /> Rush 5 min</span>}
          {aiBadge && o.payment_status === "paid" && <span data-testid="admin-order-ai-badge" className={`text-xs px-2.5 py-1 rounded-full ${o.ai_status === "failed" ? "bg-red-500/20 text-red-200" : "bg-white/10 text-slate-200"}`}>{aiBadge}</span>}
        </div>
        <a href={`mailto:${o.email}`} className="text-sm text-[#e6e8ec] break-all">{o.email}</a>
        <div className="text-sm text-slate-300">
          <span className="text-slate-400">Style:</span> {STYLES.find((s) => s.id === o.style)?.name || o.style}
          <span className="text-slate-400 ml-4">Paid:</span> {o.payment_status === "paid" ? `$${o.amount}` : "No"}
        </div>
        {o.custom_request && <p className="text-sm text-slate-300 bg-white/[0.03] rounded-xl p-3 whitespace-pre-wrap">{o.custom_request}</p>}
        <div className="flex flex-wrap items-center gap-3 pt-1">
          <Select value={o.status} onValueChange={(v) => onStatus(o.id, v)}>
            <SelectTrigger data-testid="admin-order-action-update" className="w-44 h-9 rounded-xl bg-[#0b0b0d] border-white/15 capitalize">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#0b0b0d] border-white/15 text-white">
              {STATUSES.map((s) => (
                <SelectItem key={s} value={s} data-testid={`admin-status-option-${s}`} className="capitalize">{label(s)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <a href={photo} download className="btn-ghost px-3 py-2 text-xs"><Download className="h-3.5 w-3.5" /> Original</a>
          {o.payment_status === "paid" && o.ai_status !== "processing" && (
            <button data-testid={`admin-order-regenerate-${o.id}`} onClick={() => onRegenerate(o.id)} className="btn-ghost px-3 py-2 text-xs">
              <Wand2 className="h-3.5 w-3.5" /> {o.result_path ? "Regenerate" : "Run AI"}
            </button>
          )}
          <span className="text-xs text-slate-400 ml-auto">{new Date(o.created_at).toLocaleString()}</span>
        </div>
      </div>
    </div>
  );
};

export default function AdminDashboard() {
  const nav = useNavigate();
  const [orders, setOrders] = useState(null);
  const [filter, setFilter] = useState("all");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/orders");
      setOrders(Array.isArray(data) ? data : []);
    } catch (e) {
      if ([401, 403].includes(e?.response?.status)) nav("/admin/login");
      else toast.error(errorText(e));
    }
  }, [nav]);

  useEffect(() => {
    load();
  }, [load]);

  const onStatus = async (id, status) => {
    try {
      await api.patch(`/admin/orders/${id}`, { status });
      setOrders((os) => os.map((o) => (o.id === id ? { ...o, status } : o)));
      toast.success(`Marked ${label(status)}`);
    } catch (e) {
      toast.error(errorText(e));
    }
  };

  const onRegenerate = async (id) => {
    try {
      await api.post(`/admin/orders/${id}/regenerate`);
      setOrders((os) => os.map((o) => (o.id === id ? { ...o, ai_status: "queued", status: "paid" } : o)));
      toast.success("AI edit started");
    } catch (e) {
      toast.error(errorText(e));
    }
  };

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    nav("/admin/login");
  };

  const list = (orders || []).filter((o) => filter === "all" || o.status === filter);

  return (
    <main className="min-h-screen bg-[#050505]" data-testid="admin-dashboard">
      <header className="border-b border-white/10 px-5 sm:px-8 h-[72px] flex items-center justify-between">
        <a href="/" className="flex items-center gap-3"><LogoMark className="h-8 w-8" /><span className="font-display font-bold">Orders</span></a>
        <div className="flex gap-2">
          <button data-testid="admin-refresh" onClick={load} className="btn-ghost px-4 py-2 text-sm"><RefreshCw className="h-4 w-4" /></button>
          <button data-testid="admin-logout" onClick={logout} className="btn-ghost px-4 py-2 text-sm"><LogOut className="h-4 w-4" /> Log out</button>
        </div>
      </header>
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-10">
        <div className="flex flex-wrap gap-2 mb-8" data-testid="admin-filters">
          {["all", ...STATUSES].map((s) => (
            <button
              key={s}
              data-testid={`admin-filter-${s}`}
              onClick={() => setFilter(s)}
              className={`px-4 py-2 rounded-full text-sm capitalize transition-colors ${filter === s ? "bg-[#3b4049]" : "bg-white/5 text-slate-300 hover:bg-white/10"}`}
            >
              {label(s)} {s !== "all" && `(${(orders || []).filter((o) => o.status === s).length})`}
            </button>
          ))}
        </div>
        <div className="grid gap-5" data-testid="admin-order-table">
          {orders === null && <p className="text-slate-400">Loading…</p>}
          {orders && list.length === 0 && <p className="text-slate-400" data-testid="admin-empty">No orders here yet.</p>}
          {list.map((o) => <OrderCard key={o.id} o={o} onStatus={onStatus} onRegenerate={onRegenerate} />)}
        </div>
      </div>
    </main>
  );
}
